import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { ApiError } from '../api/client';
import { getMigrationById } from '../api/migrations';
import {
  ActivateWaveResult,
  BrowserMintSession,
  activateWave,
  beginBrowserMintSession,
  waveErrorCode,
} from '../api/waves';
import { createPkcePair } from '../lib/pkce';
import {
  SignInResult,
  clearSignInPending,
  listenForSignInResult,
  markSignInPending,
  registerSignInState,
  unregisterSignInState,
} from '../lib/signInHelper';

// FD-592 (FD-ADR-047 §3) — the wave-activation BROWSER sign-in, path B.
//
// Microsoft's first-party client is the only client that can request the DRS
// token (FD-ADR-047, the constraint stated first), and it returns only to a
// loopback address. So the sign-in opens in a NEW TAB, ends on
// `http://localhost:<port>/?code=…&state=…` which nothing serves, and the code
// comes back either through the AirBridge sign-in helper extension or by the
// operator pasting that address.
//
// ── THE VERIFIER LIVES IN THIS HOOK'S MEMORY AND NOWHERE ELSE ──────────────
//
// A React ref. Never localStorage, never sessionStorage, never a URL, never a log.
// The page does not navigate during the sign-in (the sign-in is in another tab),
// so nothing needs to survive a reload — and if the operator reloads anyway, the
// verifier is gone, the page says so (useSignInHelper's orphan notice), and the
// sign-in starts again cleanly. A test spies on both storages and on history.
//
// ── AT MOST ONE ACTIVATION IN FLIGHT, AND A CODE IS REDEEMED ONCE ──────────
//
// FD-ADR-032's double-mint guard, applied to this path. A successful
// `activate-wave` mints a tenant-join credential Rubix cannot revoke; two
// overlapping calls would mint two. `inFlight` makes a second result or a second
// paste a no-op while one is running, and NOTHING here retries an
// `authorizationCode` call: Entra codes are single-use, so a retry can only ever
// be refused — or, worse, race the first. Recovery is an operator action
// ("Start over"), which mints a new session, a new state and a new verifier.
//
// The one exception is a paste the SERVER refused before redeeming anything —
// a malformed address, or an address from a different sign-in. Nothing was
// spent, so the operator may paste again. That is operator-driven, not a retry.

export type BrowserSignInPhase =
  | { kind: 'idle' }
  /** PKCE + `mint-session` + helper registration in flight. */
  | { kind: 'starting' }
  /** The sign-in tab is open; waiting for the helper or a paste. */
  | {
      kind: 'awaiting-return';
      session: BrowserMintSession;
      authorizeUrl: string;
      helperRegistered: boolean;
      /** A refused paste the operator can correct by pasting again. */
      pasteError: unknown | null;
    }
  /** The one `activate-wave` call is running — this IS the mint. */
  | { kind: 'activating'; session: BrowserMintSession }
  | { kind: 'done'; result: ActivateWaveResult }
  | { kind: 'failed'; error: unknown };

export interface BrowserSignInActivation {
  phase: BrowserSignInPhase;
  /** Begins a sign-in. Called from a click, never from an effect. */
  start: (expiryDays: number) => void;
  /** Redeem a pasted `http://localhost…` return address. */
  submitReturnUrl: (returnUrl: string) => void;
  /** Abandon the sign-in. Withdraws the helper registration; calls no Manager endpoint. */
  cancel: () => void;
}

/**
 * Server refusals that happen BEFORE any redemption — the code is still unspent,
 * so the operator may correct the paste and try again.
 */
const PASTE_RECOVERABLE: ReadonlySet<string> = new Set([
  'RETURN_URL_INVALID',
  'STATE_REQUIRED',
  'STATE_MISMATCH',
  'REDIRECT_URI_MISMATCH',
]);

/** A client-side refusal shaped like the ADR-007 envelope, so `ActivateError` renders it. */
export function localWaveError(code: string, message: string): ApiError {
  return new ApiError(400, message, {
    apiVersion: '1.0',
    requestId: 'local',
    status: 'error',
    timestamp: new Date().toISOString(),
    data: {},
    error: { message, code },
  });
}

/**
 * Check a pasted address locally before sending it. The server checks the same
 * things (and is the control); this only spares a round trip and gives the
 * operator the reason while the tab is still open.
 */
export function checkPastedReturnUrl(
  pasted: string,
  expectedState: string,
): { ok: true; url: string } | { ok: false; error: ApiError } {
  const value = pasted.trim();
  let url: URL;
  try {
    url = new URL(value);
  } catch {
    return {
      ok: false,
      error: localWaveError(
        'RETURN_URL_INVALID',
        'That is not a web address. Copy the whole address from the sign-in tab — it starts with http://localhost.',
      ),
    };
  }
  if (url.protocol !== 'http:' || url.hostname !== 'localhost') {
    return {
      ok: false,
      error: localWaveError(
        'RETURN_URL_INVALID',
        'That address is not the one the sign-in returns to. Copy the address from the sign-in tab — it starts with http://localhost.',
      ),
    };
  }
  const returnedState = url.searchParams.get('state');
  if (returnedState !== null && returnedState !== expectedState) {
    return {
      ok: false,
      error: localWaveError(
        'STATE_MISMATCH',
        'That address is from a different sign-in than the one this page started. Paste the address from the tab this page opened.',
      ),
    };
  }
  return { ok: true, url: value };
}

/** Make sure the authorize URL carries our challenge even if a Manager ignored it. */
function withChallenge(session: BrowserMintSession, challenge: string): string {
  if (session.codeChallengeIncluded) return session.authorizeUrl;
  const sep = session.authorizeUrl.includes('?') ? '&' : '?';
  return `${session.authorizeUrl}${sep}code_challenge=${encodeURIComponent(challenge)}&code_challenge_method=S256`;
}

/** An Entra refusal the helper carried back, as the same codes the server would name. */
function helperErrorAsWaveError(result: Extract<SignInResult, { kind: 'error' }>): ApiError {
  const aadsts = result.errorDescription?.match(/AADSTS\d+/)?.[0] ?? null;
  if (result.error === 'access_denied' && !aadsts) {
    return localWaveError('AUTHORIZATION_DECLINED', 'The sign-in was declined in the browser. Nothing was minted.');
  }
  const suffix = aadsts ? ` (${aadsts})` : ` (${result.error})`;
  return localWaveError(
    'AUTHORIZATION_REFUSED',
    `Microsoft Entra refused the sign-in${suffix}. Nothing was minted. The destination tenant's sign-in log for this attempt names the reason.`,
  );
}

export function useBrowserSignInActivation(
  migrationId: string,
  onDone?: () => void,
): BrowserSignInActivation {
  const [phase, setPhase] = useState<BrowserSignInPhase>({ kind: 'idle' });
  const queryClient = useQueryClient();

  // COMPONENT MEMORY ONLY. See the header.
  const verifier = useRef<string | null>(null);
  const session = useRef<BrowserMintSession | null>(null);
  const authorizeUrl = useRef<string>('');
  const helperRegistered = useRef(false);
  const expiry = useRef(30);
  const running = useRef(false);
  const inFlight = useRef(false);
  const unsubscribe = useRef<(() => void) | null>(null);

  const onDoneRef = useRef(onDone);
  onDoneRef.current = onDone;

  const forget = useCallback(() => {
    unsubscribe.current?.();
    unsubscribe.current = null;
    const state = session.current?.state;
    if (state) {
      // Deferred, deliberately: other listeners (useSignInHelper's orphan check)
      // read the pending set on the SAME message event. Clearing synchronously
      // would make a legitimate result look like an orphan to them.
      window.setTimeout(() => clearSignInPending(state), 0);
    }
    verifier.current = null;
    session.current = null;
    authorizeUrl.current = '';
    helperRegistered.current = false;
    running.current = false;
    inFlight.current = false;
  }, []);

  // Unmount abandons the sign-in: the verifier goes with the component.
  useEffect(() => () => forget(), [forget]);

  // While a sign-in is open, warn before the page unloads — a reload loses the
  // verifier. The browser shows its own generic prompt; nothing is stored.
  useEffect(() => {
    if (phase.kind !== 'awaiting-return' && phase.kind !== 'activating') return undefined;
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [phase.kind]);

  /** One authoritative read when a call fails — the response may have been lost after a real mint. */
  const reconcile = useCallback(async (): Promise<ActivateWaveResult | null> => {
    try {
      const detail = await queryClient.fetchQuery({
        queryKey: ['migration', migrationId],
        queryFn: () => getMigrationById(migrationId),
        staleTime: 0,
      });
      const m = detail.migration;
      if ((m.bprtStatus ?? '').toLowerCase() === 'active') {
        return {
          migrationId,
          bprtStatus: m.bprtStatus as string,
          expiresUtc: m.bprtExpiresUtc ?? '',
          packageUserPresent: Boolean(m.bprtPackagePid),
        };
      }
    } catch {
      // Best-effort. A failed re-check must not mask the original error.
    }
    return null;
  }, [migrationId, queryClient]);

  const succeed = useCallback(
    (result: ActivateWaveResult) => {
      forget();
      setPhase({ kind: 'done', result });
      void queryClient.invalidateQueries({ queryKey: ['migration', migrationId] });
      onDoneRef.current?.();
    },
    [forget, migrationId, queryClient],
  );

  const fail = useCallback(
    (error: unknown) => {
      forget();
      setPhase({ kind: 'failed', error });
    },
    [forget],
  );

  /** The ONE activate-wave call for this sign-in. Never retried automatically. */
  const redeem = useCallback(
    async (
      body:
        | { authorizationCode: string; redirectUri: string }
        | { returnUrl: string; state: string; redirectUri: string },
    ) => {
      const current = session.current;
      const codeVerifier = verifier.current;
      if (!running.current || !current || !codeVerifier) return;
      if (inFlight.current) return; // at most one activation in flight
      inFlight.current = true;
      setPhase({ kind: 'activating', session: current });

      try {
        const result = await activateWave(migrationId, {
          ...body,
          codeVerifier,
          expiryDays: expiry.current,
        });
        if (!running.current) return;
        succeed(result);
      } catch (err) {
        if (!running.current) return;
        const code = waveErrorCode(err);
        if ('returnUrl' in body && PASTE_RECOVERABLE.has(code)) {
          // Nothing was redeemed — the operator may correct the paste.
          inFlight.current = false;
          setPhase({
            kind: 'awaiting-return',
            session: current,
            authorizeUrl: authorizeUrl.current,
            helperRegistered: helperRegistered.current,
            pasteError: err,
          });
          return;
        }
        const recovered = await reconcile();
        if (!running.current) return;
        if (recovered) {
          succeed(recovered);
          return;
        }
        fail(err);
      }
    },
    [fail, migrationId, reconcile, succeed],
  );

  const start = useCallback(
    (expiryDays: number) => {
      if (running.current) return; // double click / StrictMode
      running.current = true;
      inFlight.current = false;
      expiry.current = expiryDays;
      setPhase({ kind: 'starting' });

      void (async () => {
        let pair;
        let begun: BrowserMintSession;
        try {
          pair = await createPkcePair();
          begun = await beginBrowserMintSession(migrationId, pair.challenge);
        } catch (err) {
          if (!running.current) return;
          fail(err);
          return;
        }
        if (!running.current) return;

        verifier.current = pair.verifier;
        session.current = begun;
        authorizeUrl.current = withChallenge(begun, pair.challenge);
        markSignInPending(begun.state);

        // Listen BEFORE registering, and register BEFORE opening the tab: the
        // extension only forwards a return whose state it already holds.
        unsubscribe.current = listenForSignInResult(begun.state, (result) => {
          if (!running.current || session.current?.state !== begun.state) return;
          if (result.kind === 'error') {
            fail(helperErrorAsWaveError(result));
            return;
          }
          void redeem({ authorizationCode: result.code, redirectUri: begun.redirectUri });
        });
        const registered = await registerSignInState(begun.state);
        if (!running.current) return;
        helperRegistered.current = registered;

        // `noopener` makes window.open return null whether or not it opened, so a
        // blocked popup is undetectable here — the stage always offers a real link.
        window.open(authorizeUrl.current, '_blank', 'noopener');

        setPhase({
          kind: 'awaiting-return',
          session: begun,
          authorizeUrl: authorizeUrl.current,
          helperRegistered: registered,
          pasteError: null,
        });
      })();
    },
    [fail, migrationId, redeem],
  );

  const submitReturnUrl = useCallback(
    (returnUrl: string) => {
      const current = session.current;
      if (!running.current || !current || inFlight.current) return;
      const checked = checkPastedReturnUrl(returnUrl, current.state);
      if (!checked.ok) {
        setPhase({
          kind: 'awaiting-return',
          session: current,
          authorizeUrl: authorizeUrl.current,
          helperRegistered: helperRegistered.current,
          pasteError: checked.error,
        });
        return;
      }
      void redeem({ returnUrl: checked.url, state: current.state, redirectUri: current.redirectUri });
    },
    [redeem],
  );

  const cancel = useCallback(() => {
    const state = session.current?.state;
    if (state && helperRegistered.current) unregisterSignInState(state);
    forget();
    setPhase({ kind: 'idle' });
  }, [forget]);

  return { phase, start, submitReturnUrl, cancel };
}
